export const DESKTOP_SIDEBAR_TOGGLE_EVENT = 'imai:window-sidebar-toggle';

export type DesktopWindowAppearance = {
  version: 1;
  title: string;
  background: string;
  foreground: string;
  sidebarWidth: number;
  sidebarVisible: boolean;
  headerInTitlebar: boolean;
  overlayTitlebar: boolean;
};

export function shouldToggleDesktopSidebar(input: {
  hasDesktopBridge: boolean;
  desktopViewport: boolean;
  mode: 'desktop' | 'mobile';
  surfaceVisible: boolean;
}): boolean {
  return (
    input.hasDesktopBridge &&
    input.desktopViewport === (input.mode === 'desktop') &&
    input.surfaceVisible
  );
}

export function normalizeDesktopWindowTitle(value: string): string {
  return (
    value
      .replace(/[\u0000-\u001f\u007f]/gu, ' ')
      .replace(/\s+/gu, ' ')
      .replace(/\s*[|·]\s*IMAI(?: Studio)?\s*$/iu, '')
      .trim()
      .slice(0, 160) || 'IMAI'
  );
}

/** The preload supplies resolved RGB pixels, never arbitrary CSS expressions. */
export function normalizeDesktopRgb(
  value: string | null,
  fallback: string
): string {
  const match =
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/u.exec(
      value ?? ''
    );
  if (!match) return fallback;
  const channels = match.slice(1).map(Number);
  if (channels.some((channel) => channel > 255)) return fallback;
  return `rgb(${channels.join(', ')})`;
}

export function buildDesktopWindowAppearance(input: {
  title: string;
  dark: boolean;
  background: string | null;
  foreground: string | null;
  headerInTitlebar?: boolean;
  overlayTitlebar?: boolean;
  sidebar: {
    left: number;
    width: number;
    height: number;
    visible: boolean;
  } | null;
}): DesktopWindowAppearance {
  const sidebar = input.sidebar;
  const sidebarVisible =
    !!sidebar &&
    sidebar.visible &&
    Number.isFinite(sidebar.left) &&
    Math.abs(sidebar.left) <= 1 &&
    Number.isFinite(sidebar.width) &&
    sidebar.width > 0 &&
    Number.isFinite(sidebar.height) &&
    sidebar.height > 0;
  return {
    version: 1,
    title: normalizeDesktopWindowTitle(input.title),
    background: normalizeDesktopRgb(
      input.background,
      input.dark ? 'rgb(0, 0, 0)' : 'rgb(250, 250, 250)'
    ),
    foreground: normalizeDesktopRgb(
      input.foreground,
      input.dark ? 'rgb(229, 229, 229)' : 'rgb(23, 23, 23)'
    ),
    sidebarWidth: sidebarVisible ? Math.min(640, Math.round(sidebar.width)) : 0,
    sidebarVisible,
    headerInTitlebar: input.headerInTitlebar === true,
    overlayTitlebar: input.overlayTitlebar === true,
  };
}
